import React, { useContext, useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Avatar,
    FormControl, Select, MenuItem, Container, Chip, Drawer,
    IconButton, InputLabel, LinearProgress, Switch, FormControlLabel, CircularProgress, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DataUsageOutlinedIcon from '@mui/icons-material/DataUsageOutlined';
import Layout from './Layout';
import PageHeader from './PageHeader';
import UserService from '../services/UserService';
import { useToast } from '../contexts/ToastContext';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';
import ClientService from '../services/ClientService';
import StripeService from '../services/StripeService';
import CustomerService from '../services/CustomerService';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './css/MyAccountPage.module.css';
import { LANGUAGES } from './enums/languageOptions';
import { glassSurfaceStrong } from '../styles/glass';

/* ── sub-components ── */

const FieldRow = ({ label, value, last }) => (
    <Box sx={{ py: 1.5, borderBottom: last ? 'none' : '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.4, lineHeight: 1 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1a2332', fontWeight: 500 }}>
            {value || '—'}
        </Typography>
    </Box>
);

// A single Apple/Notion-style clickable settings row: icon · title · value · chevron.
const SettingRow = ({ icon, title, subtitle, divider, onClick }) => (
    <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: { xs: 2, sm: 2.5 },
            py: 2,
            cursor: 'pointer',
            borderTop: divider ? '1px solid #eef2f6' : 'none',
            transition: 'background-color 0.18s ease',
            '&:hover': { backgroundColor: '#f5f9fb' },
            '&:hover .settingChevron': { transform: 'translateX(3px)', color: '#427898' },
            '&:focus-visible': { outline: '2px solid #427898', outlineOffset: '-2px' },
        }}
    >
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: '#eef4f8', color: '#1e3a52', flexShrink: 0 }}>
            {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: '#1a2332', lineHeight: 1.3 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 12.5, color: '#94a3b8', mt: 0.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
        <ChevronRightIcon className="settingChevron" sx={{ color: '#cbd5e1', fontSize: 22, flexShrink: 0, transition: 'transform 0.18s ease, color 0.18s ease' }} />
    </Box>
);

// One quiet contact line inside the profile card (icon + value).
const ContactLine = ({ icon, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ color: '#94a3b8', display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value || '—'}
        </Typography>
    </Box>
);

// Soft, blurred brand-colored light patches sitting behind the page content.
// Ambient lighting — never a visible shape.
const AmbientBackdrop = () => (
    <Box
        aria-hidden
        sx={{
            position: 'absolute',
            top: -90, left: -70, right: -70,
            height: 460,
            zIndex: 0,
            pointerEvents: 'none',
            filter: 'blur(30px)',
            background: `
                radial-gradient(540px 320px at 20% 8%, rgba(30,58,82,0.16) 0%, rgba(30,58,82,0) 60%),
                radial-gradient(460px 300px at 94% 2%, rgba(59,94,127,0.11) 0%, rgba(59,94,127,0) 62%),
                radial-gradient(520px 360px at 78% 96%, rgba(30,58,82,0.07) 0%, rgba(30,58,82,0) 64%)
            `,
        }}
    />
);

// A clean, opaque detail container: icon · title · optional Edit action, then content.
const DetailSection = ({ icon, title, editLabel, onEdit, children }) => (
    <Paper
        elevation={0}
        sx={{
            backgroundColor: '#fff',
            border: '1px solid #eef2f6',
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: '0 1px 2px rgba(16,42,66,0.04), 0 14px 30px -20px rgba(30,58,82,0.14)',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: '#eef4f8', color: '#1e3a52', flexShrink: 0 }}>
                {icon}
            </Box>
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 15, color: '#1a2332' }}>
                {title}
            </Typography>
            {onEdit && (
                <Button
                    variant="text"
                    startIcon={<EditOutlinedIcon sx={{ fontSize: 17 }} />}
                    onClick={onEdit}
                    sx={{ color: '#427898', textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '10px', '&:hover': { backgroundColor: '#eef4f8' } }}
                >
                    {editLabel}
                </Button>
            )}
        </Box>
        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 1, sm: 1.5 } }}>
            {children}
        </Box>
    </Paper>
);

const statusDotColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'active':   return '#22c55e';
        case 'trialing': return '#3b82f6';
        case 'past_due': return '#ef4444';
        case 'canceled': return '#94a3b8';
        default:         return '#94a3b8';
    }
};

/* ── main component ── */
const TABS = { PROFILE: 'profile', COMPANY: 'company', SUBSCRIPTION: 'subscription' };

const MyAccountPage = ({ section = TABS.PROFILE }) => {
    const { t, i18n } = useTranslation();
    const { role } = useContext(AuthContext);
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [drawerOpen, setDrawerOpen] = useState(null); // null | 'personal' | 'password' | 'language' | 'company' | 'banking' | 'logo'
    const [isSaving, setIsSaving] = useState(false);

    const [subscription, setSubscription]   = useState(null);
    const [customerCount, setCustomerCount] = useState(null);
    const [userData, setUserData]           = useState(null);
    const [bankingData, setBankingData]     = useState({ creditorName: '', bankName: '', iban: '', swiftBic: '', street: '', postalCode: '', town: '', bankClearingNumber: '', currency: 'CHF' });
    const [loginData, setLoginData]         = useState({});
    const [companyData, setCompanyData]     = useState(null);
    const [language, setLanguage]           = useState('EN');

    const [editUserData, setEditUserData]       = useState(null);
    const [editBankingData, setEditBankingData] = useState(null);
    const [editCompanyData, setEditCompanyData] = useState(null);
    const [editLanguage, setEditLanguage]       = useState('EN');

    const [previewLogo, setPreviewLogo] = useState(null);
    const [tempLogo, setTempLogo]       = useState(null);

    const [validationErrors, setValidationErrors] = useState({});
    const [passwordErrors, setPasswordErrors]     = useState({});
    const [personalErrors, setPersonalErrors]     = useState({});

    // ── Drawer open/close ─────────────────────────────────────────────────────

    const openDrawer = (panel) => {
        if (panel === 'personal') { setEditUserData({ ...userData }); setPersonalErrors({}); }
        if (panel === 'password') { setLoginData({}); setPasswordErrors({}); }
        if (panel === 'language') { setEditLanguage(language); }
        if (panel === 'company')  { setEditCompanyData({ ...companyData }); setValidationErrors({}); }
        if (panel === 'banking')  { setEditBankingData({ ...bankingData }); setValidationErrors({}); }
        if (panel === 'logo')     { setPreviewLogo(null); setTempLogo(null); }
        setDrawerOpen(panel);
    };

    const closeDrawer = () => {
        setDrawerOpen(null);
        setPersonalErrors({});
        setPasswordErrors({});
        setValidationErrors({});
    };

    // ── Change handlers ───────────────────────────────────────────────────────

    const handleUserChange      = (e) => setEditUserData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleCompanyChange   = (e) => setEditCompanyData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleBankingChange   = (e) => setEditBankingData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleLoginDataChange = (e) => setLoginData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    // ── Validation ────────────────────────────────────────────────────────────

    const validatePersonal = () => {
        const errors = {};
        const isEmpty = (v) => !v || v.toString().trim() === '';
        if (isEmpty(editUserData?.firstName)) errors.firstName = t('account.validation.firstNameRequired');
        if (isEmpty(editUserData?.lastName))  errors.lastName  = t('account.validation.lastNameRequired');
        if (isEmpty(editUserData?.phone))     errors.phone     = t('account.validation.phoneRequired');
        setPersonalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validatePassword = () => {
        const errors = {};
        if (!loginData?.oldPassword?.trim())        errors.oldPassword        = t('account.validation.oldPasswordRequired');
        if (!loginData?.newPassword?.trim())        errors.newPassword        = t('account.validation.newPasswordRequired');
        if (!loginData?.confirmNewPassword?.trim()) errors.confirmNewPassword = t('account.validation.confirmNewPasswordRequired');
        if (loginData?.newPassword !== loginData?.confirmNewPassword) {
            errors.newPassword        = t('account.validation.passwordsMustMatch');
            errors.confirmNewPassword = t('account.validation.passwordsMustMatch');
        }
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateCompany = () => {
        const errors = {};
        const phonePattern = /^\+?[0-9]{1,4}?[0-9\s().-]{7,15}$/;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const yearPattern  = /^(19|20)\d{2}$/;
        const isEmpty = (v) => !v || v.toString().trim() === '';
        if (!editCompanyData?.name?.trim())            errors.name           = t('account.validation.companyNameRequired');
        if (!editCompanyData?.address?.trim())         errors.address        = t('account.validation.addressRequired');
        if (isEmpty(editCompanyData?.phone))           errors.phone          = t('account.validation.phoneRequired');
        else if (!phonePattern.test(editCompanyData.phone)) errors.phone     = t('account.validation.phoneInvalid');
        if (isEmpty(editCompanyData?.email))           errors.email          = t('account.validation.emailRequired');
        else if (!emailPattern.test(editCompanyData.email)) errors.email     = t('account.validation.emailInvalid');
        if (isEmpty(editCompanyData?.website))         errors.website        = t('account.validation.websiteInvalid');
        if (isEmpty(editCompanyData?.foundationYear))  errors.foundationYear = t('account.validation.foundationYearRequired');
        else if (!yearPattern.test(editCompanyData.foundationYear.toString())) errors.foundationYear = t('account.validation.foundationYearInvalid');
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateBanking = () => {
        const errors = {};
        const ibanPattern     = /^CH\d{19}$/;
        const swiftBicPattern = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
        const bcnPattern      = /^\d{3,5}$/;
        const isEmpty = (v) => !v || v.toString().trim() === '';
        if (isEmpty(editBankingData?.creditorName))       errors.creditorName       = t('account.validation.creditorNameRequired');
        if (isEmpty(editBankingData?.bankName))           errors.bankName           = t('account.validation.bankNameRequired');
        if (isEmpty(editBankingData?.street))             errors.street             = t('account.validation.streetRequired');
        if (isEmpty(editBankingData?.postalCode))         errors.postalCode         = t('account.validation.postalCodeRequired');
        if (isEmpty(editBankingData?.town))               errors.town               = t('account.validation.townRequired');
        if (isEmpty(editBankingData?.iban))               errors.iban               = t('account.validation.ibanRequired');
        else if (!ibanPattern.test(editBankingData.iban.replace(/\s/g, ''))) errors.iban = t('account.validation.ibanInvalid');
        if (isEmpty(editBankingData?.swiftBic))           errors.swiftBic           = t('account.validation.swiftRequired');
        else if (!swiftBicPattern.test(editBankingData.swiftBic)) errors.swiftBic   = t('account.validation.swiftInvalid');
        if (isEmpty(editBankingData?.bankClearingNumber)) errors.bankClearingNumber = t('account.validation.bcnRequired');
        else if (!bcnPattern.test(editBankingData.bankClearingNumber)) errors.bankClearingNumber = t('account.validation.bcnInvalid');
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ── Save handlers ─────────────────────────────────────────────────────────

    const handleSavePersonal = async () => {
        if (!validatePersonal()) return;
        setIsSaving(true);
        try {
            const data = await UserService.savePersonalInformation(editUserData);
            setUserData(data); setEditUserData(data); closeDrawer();
            showToast(t('account.toasts.personalUpdated'), 'success');
        } catch { showToast(t('account.toasts.personalUpdateError'), 'error'); }
        finally { setIsSaving(false); }
    };

    const handleSavePassword = async () => {
        if (!validatePassword()) return;
        setIsSaving(true);
        try {
            await UserService.changePassword(loginData);
            setLoginData({}); closeDrawer();
            showToast(t('account.toasts.passwordChanged'), 'success');
        } catch { showToast(t('account.toasts.passwordChangeError'), 'error'); }
        finally { setIsSaving(false); }
    };

    const handleSaveLanguage = async () => {
        setIsSaving(true);
        try {
            await UserService.changeLanguage({ languageCode: editLanguage });
            setLanguage(editLanguage);
            i18n.changeLanguage(editLanguage.toLowerCase());
            closeDrawer();
            showToast(t('account.toasts.languageChanged'), 'success');
        } catch { showToast(t('account.toasts.languageChangeError'), 'error'); }
        finally { setIsSaving(false); }
    };

    const handleSaveCompany = async () => {
        if (!validateCompany()) return;
        setIsSaving(true);
        try {
            const data = await UserService.updateCompanyInfo(editCompanyData);
            setCompanyData(data); closeDrawer();
            showToast(t('account.toasts.companyUpdated'), 'success');
        } catch { showToast(t('account.toasts.companyUpdateError'), 'error'); }
        finally { setIsSaving(false); }
    };

    const handleSaveBanking = async () => {
        if (!validateBanking()) return;
        setIsSaving(true);
        try {
            const data = await UserService.createOrUpdateBankingInfo(editBankingData);
            setBankingData(data); closeDrawer();
            showToast(t('account.toasts.bankingUpdated'), 'success');
        } catch { showToast(t('account.toasts.bankingUpdateError'), 'error'); }
        finally { setIsSaving(false); }
    };

    const handleLogoFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { showToast(t('account.toasts.logoInvalid'), 'warning'); return; }
        setTempLogo(file);
        const reader = new FileReader();
        reader.onload = (e) => setPreviewLogo(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleSaveLogo = async () => {
        if (!tempLogo) { showToast(t('account.toasts.logoSelectFirst'), 'warning'); return; }
        setIsSaving(true);
        const formData = new FormData();
        formData.append('file', tempLogo);
        try {
            const data = await ClientService.updateLogo(formData);
            setCompanyData(prev => ({ ...prev, logo: data.logo }));
            showToast(t('account.toasts.companyUpdated'), 'success');
            closeDrawer();
        } catch { showToast(t('account.toasts.companyUpdateError'), 'error'); }
        finally { setIsSaving(false); }
    };

    // ── Data fetch ────────────────────────────────────────────────────────────

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const user = await UserService.getUserInfo();
                setUserData(user); setEditUserData(user);
                setLanguage(user.languageCode || 'EN');
                setEditLanguage(user.languageCode || 'EN');
                i18n.changeLanguage((user.languageCode || 'EN').toLowerCase());
            } catch {}
            if (role === Roles.ADMIN) {
                try {
                    const banking = await UserService.getCompanyBankInfo();
                    if (Array.isArray(banking) && banking.length > 0) { setBankingData(banking[0]); setEditBankingData(banking[0]); }
                } catch {}
                try {
                    const company = await UserService.getCompanyInfo();
                    setCompanyData(company); setEditCompanyData(company);
                } catch {}
                try {
                    const sub = await StripeService.getSubscription();
                    setSubscription(sub);
                } catch {}
                try {
                    const count = await CustomerService.countAllCustomers();
                    setCustomerCount(count);
                } catch {}
            }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Drawer metadata ───────────────────────────────────────────────────────

    const drawerMeta = {
        personal: { title: t('account.personal.title'),   subtitle: t('account.personal.subtitle',  { defaultValue: 'Update your name and contact number' }) },
        password: { title: t('account.password.title'),   subtitle: t('account.password.subtitle',  { defaultValue: 'Enter your current password to set a new one' }) },
        language: { title: t('account.language.title'),   subtitle: t('account.language.subtitle',  { defaultValue: 'Choose your preferred display language' }) },
        company:  { title: t('account.company.title'),    subtitle: t('account.company.subtitle',   { defaultValue: 'Update your company details used on documents' }) },
        banking:  { title: t('account.banking.title'),    subtitle: t('account.banking.subtitle',   { defaultValue: 'These details appear on invoice payment slips' }) },
        logo:     { title: t('account.logo.uploadTitle'), subtitle: t('account.logo.subtitle',      { defaultValue: 'Your logo appears on invoices and documents' }) },
    };

    const saveHandlers = {
        personal: handleSavePersonal,
        password: handleSavePassword,
        language: handleSaveLanguage,
        company:  handleSaveCompany,
        banking:  handleSaveBanking,
        logo:     handleSaveLogo,
    };

    const sectionTitle = section === TABS.COMPANY
        ? t('settings.tabs.company', { defaultValue: 'Company details' })
        : section === TABS.SUBSCRIPTION
            ? t('settings.tabs.subscription', { defaultValue: 'Subscription' })
            : t('settings.tabs.profile', { defaultValue: 'Profile' });

    const roleLabel = (() => {
        switch (role) {
            case Roles.ADMIN:      return t('account.profile.roles.admin',      { defaultValue: 'Administrator' });
            case Roles.ACCOUNTANT: return t('account.profile.roles.accountant', { defaultValue: 'Accountant' });
            default:               return t('account.profile.roles.user',       { defaultValue: 'Team Member' });
        }
    })();

    return (
        <Layout>
            <Container maxWidth="md" sx={{ py: 3 }}>
                <PageHeader title={sectionTitle} />

                {/* ── PROFILE ── */}
                {section === TABS.PROFILE && (
                    <Box sx={{ position: 'relative' }}>
                        <AmbientBackdrop />

                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>

                            {/* ── Profile card (the focal point — the one frosted-glass surface) ── */}
                            <Box
                                sx={{
                                    ...glassSurfaceStrong,
                                    borderRadius: '24px',
                                    p: { xs: 3, sm: 4 },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, sm: 3 }, flexWrap: 'wrap' }}>
                                    <Avatar
                                        sx={{
                                            width: 88, height: 88, flexShrink: 0,
                                            background: 'linear-gradient(135deg, #427898 0%, #1e3a52 100%)',
                                            color: '#fff', fontSize: 30, fontWeight: 700,
                                            boxShadow: '0 8px 20px -8px rgba(30,58,82,0.55)',
                                        }}
                                    >
                                        {userData?.firstName?.[0]?.toUpperCase()}{userData?.lastName?.[0]?.toUpperCase()}
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f2135', lineHeight: 1.2 }}>
                                            {userData?.firstName} {userData?.lastName}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                mt: 1, px: 1.25, py: 0.35, borderRadius: '20px',
                                                backgroundColor: 'rgba(30,58,82,0.08)',
                                            }}
                                        >
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#427898' }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a52', letterSpacing: 0.2 }}>
                                                {roleLabel}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2.5 }}>
                                            <ContactLine icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} value={userData?.username} />
                                            <ContactLine icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />} value={userData?.phone} />
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="outlined"
                                        startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
                                        onClick={() => openDrawer('personal')}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            borderColor: 'rgba(30,58,82,0.18)',
                                            color: '#1e3a52',
                                            backgroundColor: 'rgba(255,255,255,0.6)',
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontSize: 13.5,
                                            fontWeight: 600,
                                            px: 1.75,
                                            boxShadow: 'none',
                                            transition: 'background-color 0.18s ease, border-color 0.18s ease',
                                            '&:hover': { borderColor: '#427898', backgroundColor: '#eef4f8' },
                                        }}
                                    >
                                        {t('account.profile.edit', { defaultValue: 'Edit Profile' })}
                                    </Button>
                                </Box>
                            </Box>

                            {/* ── Account settings — one clean, opaque container of tappable rows ── */}
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2, mb: 1.5, ml: 0.5 }}>
                                    {t('account.settingsSection.title', { defaultValue: 'Account Settings' })}
                                </Typography>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #eef2f6',
                                        borderRadius: '18px',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 2px rgba(16,42,66,0.04), 0 14px 30px -20px rgba(30,58,82,0.14)',
                                    }}
                                >
                                    <SettingRow
                                        icon={<LockOutlinedIcon sx={{ fontSize: 20 }} />}
                                        title={t('account.password.title')}
                                        subtitle={t('account.password.rowSubtitle', { defaultValue: 'Change your password' })}
                                        onClick={() => openDrawer('password')}
                                    />
                                    <SettingRow
                                        icon={<TranslateOutlinedIcon sx={{ fontSize: 20 }} />}
                                        title={t('account.language.title')}
                                        subtitle={LANGUAGES[language] || language}
                                        divider
                                        onClick={() => openDrawer('language')}
                                    />
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ── COMPANY ── */}
                {section === TABS.COMPANY && (
                    <Box sx={{ position: 'relative' }}>
                        <AmbientBackdrop />

                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>

                            {/* ── Company identity card (the focal point — the one frosted-glass surface) ── */}
                            <Box
                                sx={{
                                    ...glassSurfaceStrong,
                                    borderRadius: '24px',
                                    p: { xs: 3, sm: 4 },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, sm: 3 }, flexWrap: 'wrap' }}>

                                    {/* Logo — with an inline camera affordance to change it. */}
                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 120, height: 88, borderRadius: '16px',
                                                backgroundColor: '#fff', border: '1px solid #e6ecf1',
                                                display: 'grid', placeItems: 'center', overflow: 'hidden',
                                                boxShadow: '0 8px 20px -12px rgba(30,58,82,0.4)',
                                            }}
                                        >
                                            {companyData?.logo
                                                ? <img src={companyData.logo} alt="Company Logo" style={{ maxWidth: '86%', maxHeight: '78%', objectFit: 'contain' }} />
                                                : <ApartmentOutlinedIcon sx={{ fontSize: 34, color: '#cbd5e1' }} />
                                            }
                                        </Box>
                                        <IconButton
                                            onClick={() => openDrawer('logo')}
                                            size="small"
                                            aria-label={t('account.logo.change')}
                                            sx={{
                                                position: 'absolute', right: -8, bottom: -8,
                                                width: 30, height: 30,
                                                backgroundColor: '#fff', border: '1px solid #e6ecf1',
                                                color: '#1e3a52',
                                                boxShadow: '0 2px 8px rgba(30,58,82,0.2)',
                                                '&:hover': { backgroundColor: '#eef4f8' },
                                            }}
                                        >
                                            <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f2135', lineHeight: 1.2 }}>
                                            {companyData?.name || '—'}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                mt: 1, px: 1.25, py: 0.35, borderRadius: '20px',
                                                backgroundColor: 'rgba(30,58,82,0.08)',
                                            }}
                                        >
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: companyData?.vatRegistered === false ? '#94a3b8' : '#22c55e' }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a52', letterSpacing: 0.2 }}>
                                                {companyData?.vatRegistered === false
                                                    ? t('account.company.vatExempt', { defaultValue: 'VAT exempt' })
                                                    : t('account.company.vatRegistered', { defaultValue: 'VAT registered' })}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2.5 }}>
                                            <ContactLine icon={<LanguageOutlinedIcon sx={{ fontSize: 18 }} />} value={companyData?.website} />
                                            <ContactLine icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} value={companyData?.email} />
                                            <ContactLine icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />} value={companyData?.phone} />
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="outlined"
                                        startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
                                        onClick={() => openDrawer('company')}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            borderColor: 'rgba(30,58,82,0.18)',
                                            color: '#1e3a52',
                                            backgroundColor: 'rgba(255,255,255,0.6)',
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontSize: 13.5,
                                            fontWeight: 600,
                                            px: 1.75,
                                            boxShadow: 'none',
                                            transition: 'background-color 0.18s ease, border-color 0.18s ease',
                                            '&:hover': { borderColor: '#427898', backgroundColor: '#eef4f8' },
                                        }}
                                    >
                                        {t('common.edit')}
                                    </Button>
                                </Box>
                            </Box>

                            {/* ── Company details — clean, opaque container ── */}
                            <DetailSection
                                icon={<ApartmentOutlinedIcon sx={{ fontSize: 20 }} />}
                                title={t('account.company.title')}
                                editLabel={t('common.edit')}
                                onEdit={() => openDrawer('company')}
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4 }}>
                                    <FieldRow label={t('account.company.fields.address')}        value={companyData?.address} last />
                                    <FieldRow label={t('account.company.fields.foundationYear')} value={companyData?.foundationYear?.toString()} last />
                                    <FieldRow label="UID"         value={companyData?.uid} last />
                                    <FieldRow label="MWST-Nummer" value={companyData?.mwstNumber} last />
                                </Box>
                            </DetailSection>

                            {/* ── Bank information — clean, opaque container ── */}
                            <DetailSection
                                icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />}
                                title={t('account.banking.title')}
                                editLabel={t('common.edit')}
                                onEdit={() => openDrawer('banking')}
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4 }}>
                                    <FieldRow label={t('account.banking.fields.creditorName')} value={bankingData?.creditorName} last />
                                    <FieldRow label={t('account.banking.fields.bankName')}     value={bankingData?.bankName} last />
                                    <FieldRow label="IBAN"                                     value={bankingData?.iban} last />
                                    <FieldRow label="QR-IBAN"                                  value={bankingData?.qrIban} last />
                                    <FieldRow label="SWIFT / BIC"                              value={bankingData?.swiftBic} last />
                                    <FieldRow label={t('account.banking.fields.bankAddress')}  value={[bankingData?.street, bankingData?.postalCode, bankingData?.town].filter(Boolean).join(', ') || '—'} last />
                                    <FieldRow label={t('account.banking.fields.bcn')}          value={bankingData?.bankClearingNumber} last />
                                    <FieldRow label={t('account.banking.fields.currency')}     value={bankingData?.currency} last />
                                </Box>
                            </DetailSection>
                        </Box>
                    </Box>
                )}

                {/* ── SUBSCRIPTION ── */}
                {section === TABS.SUBSCRIPTION && (
                    <Box sx={{ position: 'relative' }}>
                        <AmbientBackdrop />

                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>

                            {/* ── Plan card (the focal point — the one frosted-glass surface) ── */}
                            <Box
                                sx={{
                                    ...glassSurfaceStrong,
                                    borderRadius: '24px',
                                    p: { xs: 3, sm: 4 },
                                }}
                            >
                                {subscription ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, sm: 3 }, flexWrap: 'wrap' }}>
                                            <Box
                                                sx={{
                                                    width: 56, height: 56, borderRadius: '16px', flexShrink: 0,
                                                    display: 'grid', placeItems: 'center',
                                                    background: 'linear-gradient(135deg, #427898 0%, #1e3a52 100%)',
                                                    color: '#fff',
                                                    boxShadow: '0 8px 20px -10px rgba(30,58,82,0.55)',
                                                }}
                                            >
                                                <WorkspacePremiumOutlinedIcon sx={{ fontSize: 28 }} />
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f2135', lineHeight: 1.2 }}>
                                                    {subscription.planName}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.35, borderRadius: '20px', backgroundColor: 'rgba(30,58,82,0.08)' }}>
                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusDotColor(subscription.status), flexShrink: 0 }} />
                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a52', letterSpacing: 0.2, textTransform: 'capitalize' }}>
                                                            {subscription.status}
                                                        </Typography>
                                                    </Box>
                                                    {subscription.cancelAtPeriodEnd && (
                                                        <Chip label={t('account.subscription.cancelAtPeriodEnd')} size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 10 }} />
                                                    )}
                                                </Box>
                                            </Box>

                                            <Box sx={{ textAlign: 'right' }}>
                                                {subscription.priceCents === 0 ? (
                                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e3a52' }}>
                                                        {t('pricing.free')}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e3a52', lineHeight: 1.1 }}>
                                                            {(subscription.priceCents / 100).toFixed(2)} {subscription.currency?.toUpperCase()}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                                                            / {subscription.planInterval}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => navigate('/manage-subscription')}
                                                sx={{ backgroundColor: '#427898', borderRadius: '12px', textTransform: 'none', fontSize: 13.5, fontWeight: 600, px: 2.25, boxShadow: 'none', '&:hover': { backgroundColor: '#356382', boxShadow: 'none' } }}
                                            >
                                                {t('settings.managePortal')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => navigate('/pricing')}
                                                sx={{ borderColor: 'rgba(30,58,82,0.18)', color: '#1e3a52', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '12px', textTransform: 'none', fontSize: 13.5, fontWeight: 600, px: 2.25, boxShadow: 'none', '&:hover': { borderColor: '#427898', backgroundColor: '#eef4f8' } }}
                                            >
                                                {t('account.subscription.manage')}
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2.5, sm: 3 }, flexWrap: 'wrap' }}>
                                        <Box
                                            sx={{
                                                width: 56, height: 56, borderRadius: '16px', flexShrink: 0,
                                                display: 'grid', placeItems: 'center',
                                                backgroundColor: '#eef4f8', color: '#1e3a52',
                                            }}
                                        >
                                            <WorkspacePremiumOutlinedIcon sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 180 }}>
                                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f2135', lineHeight: 1.2 }}>
                                                {t('account.subscription.noSubscription')}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => navigate('/pricing')}
                                            sx={{ backgroundColor: '#427898', borderRadius: '12px', textTransform: 'none', fontSize: 13.5, fontWeight: 600, px: 2.25, boxShadow: 'none', '&:hover': { backgroundColor: '#356382', boxShadow: 'none' } }}
                                        >
                                            {t('account.subscription.manage')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* ── Billing — clean, opaque container ── */}
                            {subscription && (
                                <DetailSection
                                    icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />}
                                    title={t('account.subscription.billing', { defaultValue: 'Billing' })}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4 }}>
                                        <FieldRow label={t('account.subscription.fields.periodStart')} value={subscription.currentPeriodStart ? new Date(subscription.currentPeriodStart).toLocaleDateString() : '—'} last />
                                        <FieldRow label={t('account.subscription.fields.nextBillingDate')} value={subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : '—'} last />
                                    </Box>
                                </DetailSection>
                            )}

                            {/* ── Usage / entitlements — clean, opaque container ── */}
                            {subscription?.entitlements?.length > 0 && (
                                <DetailSection
                                    icon={<DataUsageOutlinedIcon sx={{ fontSize: 20 }} />}
                                    title={t('account.subscription.entitlements')}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, py: 1 }}>
                                        {subscription.entitlements.map((e, i) => {
                                            const isCustomers = e.resourceType?.toLowerCase() === 'max_customers';
                                            const actual = isCustomers ? customerCount : null;
                                            const pct = actual !== null && e.limitValue > 0
                                                ? Math.min(Math.round((actual / e.limitValue) * 100), 100)
                                                : null;
                                            const label = e.resourceType?.replace(/_/g, ' ');
                                            return (
                                                <Box key={i}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                            {label}
                                                        </Typography>
                                                        {actual !== null ? (
                                                            <Typography variant="caption" fontWeight={700} sx={{ color: '#427898' }}>
                                                                {actual} / {e.limitValue}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" fontWeight={600} sx={{ color: '#1a2332' }}>
                                                                {e.limitValue}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {pct !== null && (
                                                        <>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={pct}
                                                                sx={{
                                                                    height: 5, borderRadius: 3,
                                                                    backgroundColor: '#e8edf2',
                                                                    '& .MuiLinearProgress-bar': {
                                                                        backgroundColor: pct >= 90 ? '#ef4444' : '#427898',
                                                                        borderRadius: 3,
                                                                    },
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                                                                {e.limitValue - actual} remaining
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </DetailSection>
                            )}
                        </Box>
                    </Box>
                )}

                {/* ── Drawer ── */}
                <Drawer
                    anchor="right"
                    open={drawerOpen !== null}
                    onClose={closeDrawer}
                    PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
                >
                    <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0 }}>
                        <Box>
                            <Typography fontWeight={700} fontSize={16} sx={{ color: '#0F172A' }}>
                                {drawerOpen && drawerMeta[drawerOpen]?.title}
                            </Typography>
                            <Typography fontSize={12} sx={{ color: '#94A3B8', mt: 0.3 }}>
                                {drawerOpen && drawerMeta[drawerOpen]?.subtitle}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={closeDrawer} sx={{ color: '#94A3B8', mt: -0.25 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>

                        {/* Personal */}
                        {drawerOpen === 'personal' && (
                            <Stack spacing={2}>
                                <TextField fullWidth size="small" label={`${t('account.fields.firstName')} *`} name="firstName" value={editUserData?.firstName ?? ''} onChange={handleUserChange} error={!!personalErrors.firstName} helperText={personalErrors.firstName || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.fields.lastName')} *`}  name="lastName"  value={editUserData?.lastName  ?? ''} onChange={handleUserChange} error={!!personalErrors.lastName}  helperText={personalErrors.lastName  || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.fields.phone')} *`}     name="phone"     value={editUserData?.phone     ?? ''} onChange={handleUserChange} error={!!personalErrors.phone}     helperText={personalErrors.phone     || ' '} inputProps={{ inputMode: 'tel' }} />
                            </Stack>
                        )}

                        {/* Password */}
                        {drawerOpen === 'password' && (
                            <Stack spacing={2}>
                                <TextField fullWidth size="small" label={t('account.fields.oldPassword')}        type="password" name="oldPassword"        value={loginData?.oldPassword        || ''} onChange={handleLoginDataChange} error={!!passwordErrors.oldPassword}        helperText={passwordErrors.oldPassword        || ' '} />
                                <TextField fullWidth size="small" label={t('account.fields.newPassword')}        type="password" name="newPassword"        value={loginData?.newPassword        || ''} onChange={handleLoginDataChange} error={!!passwordErrors.newPassword}        helperText={passwordErrors.newPassword        || ' '} />
                                <TextField fullWidth size="small" label={t('account.fields.confirmNewPassword')} type="password" name="confirmNewPassword" value={loginData?.confirmNewPassword || ''} onChange={handleLoginDataChange} error={!!passwordErrors.confirmNewPassword} helperText={passwordErrors.confirmNewPassword || ' '} />
                            </Stack>
                        )}

                        {/* Language */}
                        {drawerOpen === 'language' && (
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('account.language.title')}</InputLabel>
                                <Select
                                    value={editLanguage}
                                    label={t('account.language.title')}
                                    onChange={(e) => setEditLanguage(e.target.value)}
                                >
                                    {Object.entries(LANGUAGES).map(([key, value]) => (
                                        <MenuItem key={key} value={key}>{value}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Company */}
                        {drawerOpen === 'company' && (
                            <Stack spacing={2}>
                                <TextField fullWidth size="small" label={`${t('account.company.fields.name')} *`}           name="name"           value={editCompanyData?.name           || ''} onChange={handleCompanyChange} error={!!validationErrors.name}           helperText={validationErrors.name           || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.company.fields.address')} *`}        name="address"        value={editCompanyData?.address        || ''} onChange={handleCompanyChange} error={!!validationErrors.address}        helperText={validationErrors.address        || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.company.fields.foundationYear')} *`} name="foundationYear" value={editCompanyData?.foundationYear || ''} onChange={handleCompanyChange} error={!!validationErrors.foundationYear} helperText={validationErrors.foundationYear || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.company.fields.phone')} *`}          name="phone"          value={editCompanyData?.phone          || ''} onChange={handleCompanyChange} error={!!validationErrors.phone}          helperText={validationErrors.phone          || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.company.fields.email')} *`}          name="email"          value={editCompanyData?.email          || ''} onChange={handleCompanyChange} error={!!validationErrors.email}          helperText={validationErrors.email          || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.company.fields.website')} *`}        name="website"        value={editCompanyData?.website        || ''} onChange={handleCompanyChange} error={!!validationErrors.website}        helperText={validationErrors.website        || ' '} />
                                <TextField fullWidth size="small" label="UID (e.g. CHE-123.456.789)"  name="uid"        value={editCompanyData?.uid        || ''} onChange={handleCompanyChange} helperText=" " />
                                <TextField fullWidth size="small" label="MWST-Nummer"                 name="mwstNumber" value={editCompanyData?.mwstNumber || ''} onChange={handleCompanyChange} helperText=" " />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editCompanyData?.vatRegistered !== false}
                                            onChange={(e) => setEditCompanyData(prev => ({ ...prev, vatRegistered: e.target.checked }))}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#427898' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#427898' } }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: 13, color: '#1a2332' }}>VAT Registered (MWST-pflichtig)</Typography>}
                                />
                            </Stack>
                        )}

                        {/* Banking */}
                        {drawerOpen === 'banking' && (
                            <Stack spacing={2}>
                                <TextField fullWidth size="small" label={`${t('account.banking.fields.creditorName')} *`} name="creditorName"       value={editBankingData?.creditorName       || ''} onChange={handleBankingChange} error={!!validationErrors.creditorName}       helperText={validationErrors.creditorName       || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.banking.fields.bankName')} *`}     name="bankName"           value={editBankingData?.bankName           || ''} onChange={handleBankingChange} error={!!validationErrors.bankName}           helperText={validationErrors.bankName           || ' '} />
                                <TextField fullWidth size="small" label="IBAN *"                                           name="iban"               value={editBankingData?.iban               || ''} onChange={handleBankingChange} error={!!validationErrors.iban}               helperText={validationErrors.iban               || ' '} />
                                <TextField fullWidth size="small" label="SWIFT / BIC *"                                    name="swiftBic"           value={editBankingData?.swiftBic           || ''} onChange={handleBankingChange} error={!!validationErrors.swiftBic}           helperText={validationErrors.swiftBic           || ' '} />
                                <TextField fullWidth size="small" label={`${t('account.banking.fields.street')} *`}       name="street"             value={editBankingData?.street             || ''} onChange={handleBankingChange} error={!!validationErrors.street}             helperText={validationErrors.street             || ' '} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
                                    <TextField fullWidth size="small" label={`${t('account.banking.fields.postalCode')} *`} name="postalCode" value={editBankingData?.postalCode || ''} onChange={handleBankingChange} error={!!validationErrors.postalCode} helperText={validationErrors.postalCode || ' '} />
                                    <TextField fullWidth size="small" label={`${t('account.banking.fields.town')} *`}       name="town"       value={editBankingData?.town       || ''} onChange={handleBankingChange} error={!!validationErrors.town}       helperText={validationErrors.town       || ' '} />
                                </Box>
                                <TextField fullWidth size="small" label={`${t('account.banking.fields.bcn')} *`}      name="bankClearingNumber" value={editBankingData?.bankClearingNumber || ''} onChange={handleBankingChange} error={!!validationErrors.bankClearingNumber} helperText={validationErrors.bankClearingNumber || ' '} />
                                <TextField fullWidth size="small" label={t('account.banking.fields.currency')}        name="currency"           value={editBankingData?.currency           || 'CHF'} disabled helperText=" " />
                                <TextField fullWidth size="small" label="QR-IBAN (Swiss QR invoicing)"                name="qrIban"             value={editBankingData?.qrIban             || ''} onChange={handleBankingChange} helperText="Optional — used instead of IBAN when generating QR payment slips" />
                            </Stack>
                        )}

                        {/* Logo */}
                        {drawerOpen === 'logo' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
                                <Box className={styles.logoUploadBox}>
                                    {previewLogo
                                        ? <img src={previewLogo} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        : <Typography variant="caption" sx={{ color: '#cbd5e1' }}>{t('account.logo.selectImage')}</Typography>
                                    }
                                </Box>
                                <Button variant="outlined" component="label"
                                    sx={{ borderColor: '#427898', color: '#427898', borderRadius: '8px', textTransform: 'none' }}>
                                    {t('account.logo.selectImage')}
                                    <input type="file" hidden onChange={handleLogoFileChange} />
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', px: 3, py: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                        <Button onClick={closeDrawer} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 500 }}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={drawerOpen ? saveHandlers[drawerOpen] : undefined}
                            disabled={isSaving}
                            sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                        >
                            {isSaving
                                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                                : t('buttons.save')}
                        </Button>
                    </Box>
                </Drawer>
            </Container>
        </Layout>
    );
};

export default MyAccountPage;
